import pickBy from 'lodash/pickBy';
import filter from 'lodash/filter';
import find from 'lodash/find';
import differenceBy from 'lodash/differenceBy';
import {
  GET_WORKFLOWS_START,
  GET_WORKFLOWS_SUCCESS,
  GET_WORKFLOWS_FAIL,
  CREATE_WORKFLOW_START,
  CREATE_WORKFLOW_SUCCESS,
  CREATE_WORKFLOW_FAIL,
  UPDATE_WORKFLOW_START,
  UPDATE_WORKFLOW_FAIL,
  UPDATE_WORKFLOW_SUCCESS,
  DELETE_WORKFLOW_START,
  DELETE_WORKFLOW_SUCCESS,
  DELETE_WORKFLOW_FAIL,
  GET_EDITABLE_WORKFLOWS_START,
  GET_EDITABLE_WORKFLOWS_SUCCESS,
  GET_EDITABLE_WORKFLOWS_FAIL,
  WORKFLOW_LIST_ITEM_UPDATE_POSITION,
  RESET_SEARCH_IN_WORKFLOWS,
} from 'actions/workflows';
import findIndex from 'lodash/findIndex';
import {
  IMPORT_WORKFLOW_START,
  IMPORT_WORKFLOW_SUCCESS,
  IMPORT_WORKFLOW_FAIL,
} from 'actions/workflows/importWorkflow';
import { PlanType } from '@xmatters/common/constants/plans';
import { ProcessStatus } from '@xmatters/common/constants/commonStatus';

function getInitialWorkflowsState() {
  return {
    allWorkflows: {
      status: ProcessStatus.LOADING,
      offset: 0,
      total: 0,
      list: [],
    },
    editableWorkflows: {
      status: ProcessStatus.LOADING,
      offset: 0,
      total: 0,
      list: [],
    },
    workflowCreateStatus: 'NOT_CREATED',
    workflowUpdateStatus: 'NOT_UPDATED',
    workflowDeleteStatus: 'NOT_DELETED',
    workflowImportStatus: 'NOT_IMPORTED',
  };
}

function getListAfterUpdate(currentList, action) {
  const updatedWorkflowProperties = pickBy(
    action.payload,
    value => value !== undefined,
  );
  return currentList.list.map(workflow => {
    if (workflow.id === updatedWorkflowProperties.id) {
      return {
        ...workflow,
        ...updatedWorkflowProperties,
      };
    }
    return workflow;
  });
}

function updateWorkflowOnDelete(currentList, action) {
  const deletedWorkflow = action.payload;
  const updatedList = filter(
    currentList.list,
    workflow => workflow.id !== deletedWorkflow.id,
  );

  const updatedOffset =
    find(currentList.list, workflow => workflow.id === deletedWorkflow.id) &&
    currentList.offset > 0
      ? currentList.offset - 1
      : currentList.offset;

  const updatedTotal =
    currentList.total > 0 ? currentList.total - 1 : currentList.total;

  return { list: updatedList, offset: updatedOffset, total: updatedTotal };
}

function getIndexAfterImport(currentIndex, action) {
  const { workflow } = action.payload;

  return workflow.planType !== PlanType.SYSTEM
    ? currentIndex + 1
    : currentIndex;
}

function updateWorkflowOnImport(currentList, action) {
  const { workflow } = action.payload;

  const updatedList =
    workflow.planType === PlanType.SYSTEM
      ? currentList.list
      : [workflow, ...currentList.list];

  return {
    list: updatedList,
    offset: getIndexAfterImport(currentList.offset, action),
    total: getIndexAfterImport(currentList.total, action),
  };
}

function moveWorkflowInList(workflowList, fromPosition, toPosition) {
  const newWorkflowList = [...workflowList];

  // splice workflow into the correct new position
  const workflow = newWorkflowList[fromPosition];
  newWorkflowList.splice(fromPosition, 1);
  newWorkflowList.splice(toPosition, 0, workflow);

  return newWorkflowList;
}

function getUpdatedListOfWorkflows(stateWorkflowsList, workflowListFromXmapi) {
  const results = [];

  stateWorkflowsList.forEach(localWorkflow => {
    const workflowFromXmApi = workflowListFromXmapi.find(
      workflow => workflow.id === localWorkflow.id,
    );

    if (workflowFromXmApi) {
      results.push(workflowFromXmApi);
    } else {
      results.push(localWorkflow);
    }
  });

  const remainingWorkflowFromXmApi = differenceBy(
    workflowListFromXmapi,
    results,
    'id',
  );

  return [...results, ...remainingWorkflowFromXmApi];
}

function workflowsReducer(state = getInitialWorkflowsState(), action) {
  switch (action.type) {
    case RESET_SEARCH_IN_WORKFLOWS: {
      return {
        ...state,
        allWorkflows: {
          status: ProcessStatus.LOADING,
          offset: 0,
          total: 0,
          list: [],
        },
        editableWorkflows: {
          status: ProcessStatus.LOADING,
          offset: 0,
          total: 0,
          list: [],
        },
      };
    }
    case GET_WORKFLOWS_START: {
      return {
        ...state,
        allWorkflows: {
          ...state.allWorkflows,
          status: ProcessStatus.LOADING,
        },
      };
    }
    case GET_WORKFLOWS_SUCCESS: {
      return {
        ...state,
        allWorkflows: {
          status: ProcessStatus.LOADED,
          list: getUpdatedListOfWorkflows(
            state.allWorkflows.list,
            action.payload.data,
          ),
          offset: state.allWorkflows.offset + action.payload?.count,
          total: action.payload?.total,
        },
      };
    }
    case GET_WORKFLOWS_FAIL: {
      return {
        ...state,
        allWorkflows: {
          ...state.allWorkflows,
          status: ProcessStatus.ERROR,
        },
      };
    }
    case GET_EDITABLE_WORKFLOWS_START: {
      return {
        ...state,
        editableWorkflows: {
          ...state.editableWorkflows,
          status: ProcessStatus.LOADING,
        },
      };
    }
    case GET_EDITABLE_WORKFLOWS_SUCCESS: {
      return {
        ...state,
        editableWorkflows: {
          status: ProcessStatus.LOADED,
          list: getUpdatedListOfWorkflows(
            state.editableWorkflows.list,
            action.payload.data,
          ),
          offset: state.editableWorkflows.offset + action.payload?.count,
          total: action.payload?.total,
        },
      };
    }
    case GET_EDITABLE_WORKFLOWS_FAIL: {
      return {
        ...state,
        editableWorkflows: {
          ...state.editableWorkflows,
          status: ProcessStatus.ERROR,
        },
      };
    }
    case UPDATE_WORKFLOW_START: {
      return {
        ...state,
        workflowUpdateStatus: 'UPDATING',
      };
    }
    case UPDATE_WORKFLOW_SUCCESS: {
      return {
        ...state,
        workflowUpdateStatus: 'UPDATED',
        allWorkflows: {
          ...state.allWorkflows,
          list: getListAfterUpdate(state.allWorkflows, action),
        },
        editableWorkflows: {
          ...state.editableWorkflows,
          list: getListAfterUpdate(state.editableWorkflows, action),
        },
      };
    }
    case UPDATE_WORKFLOW_FAIL: {
      return {
        ...state,
        workflowUpdateStatus: 'ERROR',
      };
    }
    case DELETE_WORKFLOW_START: {
      return {
        ...state,
        workflowDeleteStatus: 'DELETING',
      };
    }
    case DELETE_WORKFLOW_SUCCESS: {
      return {
        ...state,
        workflowDeleteStatus: 'DELETED',
        allWorkflows: {
          ...state.allWorkflows,
          ...updateWorkflowOnDelete(state.allWorkflows, action),
        },
        editableWorkflows: {
          ...state.editableWorkflows,
          ...updateWorkflowOnDelete(state.editableWorkflows, action),
        },
      };
    }
    case DELETE_WORKFLOW_FAIL: {
      return {
        ...state,
        workflowDeleteStatus: 'ERROR',
      };
    }
    case CREATE_WORKFLOW_START: {
      return {
        ...state,
        workflowCreateStatus: 'CREATING',
      };
    }
    case CREATE_WORKFLOW_SUCCESS: {
      return {
        ...state,
        workflowCreateStatus: 'CREATED',
      };
    }
    case CREATE_WORKFLOW_FAIL: {
      return {
        ...state,
        workflowCreateStatus: 'ERROR',
      };
    }
    case IMPORT_WORKFLOW_START: {
      return {
        ...state,
        workflowImportStatus: 'IMPORTING',
      };
    }
    case IMPORT_WORKFLOW_SUCCESS: {
      return {
        ...state,
        workflowImportStatus: 'IMPORTED',
        allWorkflows: {
          ...state.allWorkflows,
          ...updateWorkflowOnImport(state.allWorkflows, action),
        },
        editableWorkflows: {
          ...state.editableWorkflows,
          ...updateWorkflowOnImport(state.editableWorkflows, action),
        },
      };
    }
    case IMPORT_WORKFLOW_FAIL: {
      return {
        ...state,
        workflowImportStatus: 'ERROR',
      };
    }
    case WORKFLOW_LIST_ITEM_UPDATE_POSITION: {
      const { workflowId, position } = action.payload;
      const { allWorkflows, editableWorkflows } = state;
      const workflowIndex = findIndex(allWorkflows.list, ['id', workflowId]);
      if (workflowIndex < 0) {
        return state;
      }

      const newAllWorkflows = moveWorkflowInList(
        allWorkflows.list,
        workflowIndex,
        position,
      );

      // this is for editable list. If the item doesn't exist, we should still proceed
      let tempEditableWorkflows = {};
      const workflowEditableIndex = findIndex(editableWorkflows.list, [
        'id',
        workflowId,
      ]);
      if (workflowEditableIndex > 0) {
        const newWorkflowEditableList = moveWorkflowInList(
          editableWorkflows.list,
          workflowEditableIndex,
          position,
        );
        tempEditableWorkflows = {
          editableWorkflows: {
            ...editableWorkflows,
            list: newWorkflowEditableList,
          },
        };
      } else {
        tempEditableWorkflows = {};
      }

      return {
        ...state,
        allWorkflows: { ...allWorkflows, list: newAllWorkflows },
        ...tempEditableWorkflows,
      };
    }
    default:
      return state;
  }
}

export default workflowsReducer;
